// getRemoteConfigComputedData.js
'use strict';
import { Collection, Map } from 'immutable';

import {
  FLIX_SPLASH_DASH,
  FLIX_SPLASH_HLS,
  PUBLIC_LOGO_URL_PATH,
  LOGIN_METHODS_PRIMARY,
  LOGIN_METHODS_SECONDARY,
  SIGNUP_METHODS_PRIMARY,
  SIGNUP_METHODS_SECONDARY,
  FEATURE_WEB_AUTHN,
  POST_DIAMOND_MIN,
  SUPPORTED_LANGUAGES,
  CP_REPLY_DIAMOND_MIN_MAX_INTERVAL,
  SUPER_CHAT_RELATED_CONFIGS,
  MESSAGE_CATEGORY_GROUPINGS,
} from '../RemoteConfigKeys.js';

import createCachedSelector, {
  createSelector,
} from '../resource/createCachedSelector.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { AuthId } from '../resource/loginRegisterConstants.js';
import { castConfigValue } from '../resource/castConfigValue.js';

import getRemoteConfigData, {
  getMergedRemoteConfig,
} from '../selector/getRemoteConfigData.js';

const defaultMap = new Map();

/**
 * Select splash video URLs (hls and dash).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {object} { hls, dash }
 */
const getSplashVideo = createSelector(
  state => getRemoteConfigData(state, FLIX_SPLASH_HLS),
  state => getRemoteConfigData(state, FLIX_SPLASH_DASH),
  state => getRemoteConfigData(state, PUBLIC_LOGO_URL_PATH),
  (hls, dash, publicLogoUrlPath) => {
    const fallbackUrl = getResourceUrl({
      path: publicLogoUrlPath,
      endpoint: '/splash.mp4',
      resourceType: PUBLIC,
    });
    return {
      hls: hls || fallbackUrl.href,
      dash: dash || fallbackUrl.href,
    };
  }
);

/**
 * Select auth methods by key with APPLE_ID mapped to APPLE_ID_NEW.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} key - remote config key (e.g. LOGIN_METHODS_PRIMARY).
 * @return {string[]} List of auth method ids.
 */
const getAuthMethods = createCachedSelector(
  (state, key) => getRemoteConfigData(state, key),
  (methods = EMPTY_ARRAY) =>
    methods.map(id => (id === AuthId.APPLE_ID ? AuthId.APPLE_ID_NEW : id))
)((state, key = '') => `${key}`);

/**
 * Select all allowed login methods (deduplicated).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {string[]} Deduplicated list of login method ids.
 */
const getAllowedLoginMethods = createSelector(
  state => getAuthMethods(state, LOGIN_METHODS_PRIMARY),
  state => getAuthMethods(state, LOGIN_METHODS_SECONDARY),
  state => getAuthMethods(state, SIGNUP_METHODS_PRIMARY),
  state => getAuthMethods(state, SIGNUP_METHODS_SECONDARY),
  state => getRemoteConfigData(state, FEATURE_WEB_AUTHN),
  (
    loginPrimary = EMPTY_ARRAY,
    loginSecondary = EMPTY_ARRAY,
    signupPrimary = EMPTY_ARRAY,
    signupSecondary = EMPTY_ARRAY,
    isWebAuthnEnabled
  ) => {
    const methods = [
      isWebAuthnEnabled ? AuthId.WEB_AUTHN : '',
      ...loginPrimary,
      ...loginSecondary,
      ...signupPrimary,
      ...signupSecondary,
    ].filter(item => item);
    return [...new Set(methods)];
  }
);

/**
 * Select payment source style config (PAYMENT_COLORS_* keys).
 * Values are normalized to arrays (e.g. ['319faa', '319faa', 'ffffff']).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {object} Map of PAYMENT_COLORS_* keys to string[] values.
 */
const getPaymentSourceStyle = createSelector(
  state => getMergedRemoteConfig(state),
  (remoteConfig = defaultMap) => {
    return [...remoteConfig.keys()]
      .filter(key => key.startsWith('PAYMENT_COLORS_'))
      .reduce((acc, curr) => {
        const raw = remoteConfig.get(curr);
        const value = raw instanceof Collection ? raw.toJS() : raw;
        acc[curr] = Array.isArray(value) ? value : value.split(' ');
        return acc;
      }, {});
  }
);

/**
 * Select sibling category IDs by categoryId.
 * Sibling categories share the same MESSAGE_CATEGORY_GROUPING_* remote config group.
 * Memoized per categoryId via re-reselect cache key.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} categoryId - the category to find siblings for.
 * @return {string[]} array of sibling category ids, or EMPTY_ARRAY if not grouped.
 */
const getSiblingCategoryIds = createCachedSelector(
  state => getMergedRemoteConfig(state),
  state => getRemoteConfigData(state, MESSAGE_CATEGORY_GROUPINGS),
  (_, categoryId) => categoryId,
  (remoteConfigMap = defaultMap, categoryGroups, categoryId) => {
    for (const groupName of categoryGroups || []) {
      const dataKey = `MESSAGE_CATEGORY_GROUPING_${groupName.toUpperCase()}`;
      const raw = remoteConfigMap.get(dataKey);
      if (raw == null) continue;
      const items = castConfigValue({
        dataKey,
        value: raw instanceof Collection ? raw.toJS() : raw,
      });
      if (items.includes(categoryId)) return items;
    }
    return EMPTY_ARRAY;
  }
)((_, categoryId) => categoryId);

const SHOP_BADGE_COLOR_CONFIG = {
  shopColorBadges: {
    keyPrefix: 'SHOP_COLOR_BADGES_',
    defaultResult: {
      day_limited: 'CF0404',
      week_limited: 'CF0404',
      month_limited: 'CF0404',
      discount: 'FF7878',
      default: 'E3E3E3',
    },
  },
  shopColorBadgesV2: {
    keyPrefix: 'SHOP_COLOR_BADGES_V2_',
    defaultResult: {
      hot: '8238BACC',
      extra: 'FF6200CC',
      discount: 'F6418DCC',
      default: 'E3E3E3',
    },
  },
  shopColorPaymentBadgesV2: {
    keyPrefix: 'SHOP_COLOR_PAYMENT_BADGES_V2_',
    defaultResult: {
      fee_free: '8238BACC',
      new: 'F6418DCC',
      installment_supported: 'F6418DCC',
      default: 'E3E3E3',
    },
  },
};

/**
 * Select shop color badge mapping by badge type.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} badgeType - one of shopColorBadges, shopColorBadgesV2, shopColorPaymentBadgesV2.
 * @return {object} Map of badge name → color code.
 */
const getShopColorBadges = createCachedSelector(
  state => getMergedRemoteConfig(state),
  (state, badgeType) => badgeType,
  (remoteConfig = defaultMap, badgeType) => {
    const config = SHOP_BADGE_COLOR_CONFIG[badgeType];
    if (!config) return {};
    const { keyPrefix, defaultResult } = config;
    return [...remoteConfig.keys()]
      .filter(key => key.startsWith(keyPrefix))
      .reduce(
        (acc, curr) => {
          const raw = remoteConfig.get(curr);
          const value = raw instanceof Collection ? raw.toJS() : raw;
          const badges = Array.isArray(value)
            ? value
            : typeof value === 'string'
              ? value.split(' ')
              : [];
          badges.forEach(badge => {
            acc[badge] = curr.replace(keyPrefix, '');
          });
          return acc;
        },
        { ...defaultResult }
      );
  }
)((state, badgeType = '') => `${badgeType}`);

/**
 * Select post diamond data (price or duration checkpoints).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} dataKey - POST_DIAMOND_MIN, POST_DIAMOND_MAX,
 *   POST_DIAMOND_INTERVAL, POST_DIAMOND_DEFAULT, or 'postVideoDurationCheckpoints'.
 * @return {number[]} Sorted list of price or duration values.
 */
const getPostDiamondData = createCachedSelector(
  (state, dataKey) => {
    const sourceKey =
      dataKey === 'postVideoDurationCheckpoints' ? POST_DIAMOND_MIN : dataKey;
    return getRemoteConfigData(state, sourceKey);
  },
  (state, dataKey) => dataKey,
  (items = EMPTY_ARRAY, dataKey) => {
    if (!items.length) return [0];
    const field =
      dataKey === 'postVideoDurationCheckpoints' ? 'duration' : 'price';
    return items
      .map(item => {
        if (typeof item === 'number') return { duration: 0, price: item };
        const match = /(?<duration>[\d]+)s:(?<price>[\d]+)/.exec(item);
        return match
          ? {
              duration: Number(match.groups.duration),
              price: Number(match.groups.price),
            }
          : { duration: 0, price: Number(item) };
      })
      .sort((a, b) => b.duration - a.duration)
      .map(item => item[field]);
  }
)((state, dataKey = '') => `${dataKey}`);

/**
 * Select supported languages with zh-hant/zh mapped to zh-TW/zh-CN.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {string[]} List of normalized language codes.
 */
const getSupportedLanguages = createSelector(
  state => getRemoteConfigData(state, SUPPORTED_LANGUAGES),
  (languages = EMPTY_ARRAY) =>
    languages.map(lang => {
      if (lang.toLowerCase() === 'zh-hant') return 'zh-TW';
      if (lang.toLowerCase() === 'zh') return 'zh-CN';
      return lang;
    })
);

/**
 * Select creator reply price config by field.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} field - 'min', 'max', or 'interval'.
 * @return {number|undefined} The requested value.
 */
const getCreatorReplyPrice = createCachedSelector(
  state => getRemoteConfigData(state, CP_REPLY_DIAMOND_MIN_MAX_INTERVAL),
  (state, field) => field,
  (values = EMPTY_ARRAY, field) => {
    const [min, max, interval] = values;
    if (field === 'min') return min;
    if (field === 'max') return max;
    if (field === 'interval') return interval;
  }
)((state, field = '') => `${field}`);

/**
 * Select parsed super chat config from SUPER_CHAT_RELATED_CONFIGS.
 *
 * Each raw config item is a colon-separated string: "diamonds:displaySeconds:borderColor:textLength".
 *
 * - field='diamonds': returns sorted diamond amounts as number[].
 *   e.g. [10, 50, 100, 500]
 * - field='options': returns config keyed by diamond amount.
 *   e.g. { 10: { diamonds: 10, displaySeconds: 5, borderColor: '#ff0', textLength: 50 }, ... }
 *
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} field - 'diamonds' or 'options'.
 * @return {number[]|object} Sorted diamond amounts or option data map.
 */
const getSuperChatConfig = createCachedSelector(
  state => getRemoteConfigData(state, SUPER_CHAT_RELATED_CONFIGS),
  (state, field) => field,
  (configs = EMPTY_ARRAY, field) => {
    const parsed = configs.map(item => {
      const [diamonds, displaySeconds, borderColor, textLength] =
        item.split(':');
      return {
        diamonds: Number(diamonds),
        displaySeconds: Number(displaySeconds),
        borderColor,
        textLength: Number(textLength),
      };
    });
    if (field === 'diamonds') {
      return parsed.map(item => item.diamonds).sort((a, b) => a - b);
    }
    if (field === 'options') {
      return parsed.reduce((acc, item) => {
        acc[item.diamonds] = item;
        return acc;
      }, {});
    }
  }
)((state, field = '') => `${field}`);

/**
 * Select swag footer link data by link type key.
 *
 * Given SWAG_ABOUT_LINKS = "faq terms privacy" and remote config keys like:
 *   SWAG_ABOUT_LINKS_FAQ_URL = "/faq"
 *   SWAG_ABOUT_LINKS_FAQ_MIXPANEL_BUTTON_ID = "button-faq"
 *
 * Returns:
 * [
 *   {
 *     title: "faq",
 *     url: "/faq",
 *     mixpanel: { "button.id": "button-faq" },
 *   },
 *   { title: "terms", mixpanel: {}, ... },
 *   { title: "privacy", mixpanel: {}, ... },
 * ]
 *
 * Keys matching `${dataKey}_${ITEM}_MIXPANEL_*` are grouped into
 * the `mixpanel` object with underscores converted to dots
 * (e.g. MIXPANEL_BUTTON_ID → "button.id");
 * all other keys are set as direct properties.
 *
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} dataKey - one of SWAG_SOCIAL_MEDIA_LINKS, SWAG_ABOUT_LINKS,
 *   SWAG_SUPPORT_LINKS, SWAG_WORK_LINKS, SWAG_DISCOVER_LINKS.
 * @return {object[]} Enriched link objects.
 */
const getFooterLinkConfigs = createCachedSelector(
  (state, dataKey) => getRemoteConfigData(state, dataKey),
  state => getMergedRemoteConfig(state),
  (state, dataKey) => dataKey,
  (items = EMPTY_ARRAY, remoteConfig = defaultMap, dataKey) => {
    const remoteConfigKeys = [...remoteConfig.keys()];
    return items.map(item => {
      const relatedKeyPrefix = `${dataKey}_${item.toUpperCase()}_`;
      const relatedEntries = remoteConfigKeys
        .filter(key => key.startsWith(relatedKeyPrefix))
        .map(remoteConfigKey => {
          const key = remoteConfigKey
            .replace(relatedKeyPrefix, '')
            .toLowerCase();
          const raw = remoteConfig.get(remoteConfigKey);
          const value = raw instanceof Collection ? raw.toJS() : raw;
          return [key, value];
        });
      const object = {
        title: item,
        mixpanel: {},
      };
      relatedEntries.forEach(([key, value]) => {
        const mixpanelPrefix = 'mixpanel_';
        if (key.startsWith(mixpanelPrefix)) {
          const mixpanelProperty = key
            .replace(mixpanelPrefix, '')
            .replace('_', '.');
          object.mixpanel = {
            ...object.mixpanel,
            [mixpanelProperty]: value,
          };
        } else {
          object[key] = value;
        }
      });
      return object;
    });
  }
)((state, dataKey = '') => dataKey);

export {
  getSplashVideo,
  getAuthMethods,
  getAllowedLoginMethods,
  getPaymentSourceStyle,
  getSiblingCategoryIds,
  getShopColorBadges,
  getPostDiamondData,
  getSupportedLanguages,
  getCreatorReplyPrice,
  getSuperChatConfig,
  getFooterLinkConfigs,
};
